<template>
  <!-- One background Docker operation (run / deploy / stack action): polls until it
       finishes, shows its live output, then emits `done` with the final state. -->
  <div class="kvm-dop" :class="'kvm-dop--' + state">
    <div class="d-flex align-center">
      <v-progress-circular v-if="state === 'running'" indeterminate size="16" width="2" color="primary" class="mr-2" aria-hidden="true" />
      <v-icon v-else small :color="state === 'succeeded' ? 'success' : 'error'" class="mr-2" aria-hidden="true">
        {{ state === 'succeeded' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
      </v-icon>
      <span class="body-2 font-weight-medium mr-2">{{ label }}</span>
      <span class="kvm-dop__state text-caption mr-auto" role="status" aria-live="polite">{{ stateText }}</span>
      <gg-btn text small class="kvm-dop__btn" :aria-expanded="showOutput ? 'true' : 'false'" @click="showOutput = !showOutput">
        {{ showOutput ? 'Hide output' : 'Show output' }}
      </gg-btn>
      <gg-btn v-if="state !== 'running'" icon small aria-label="Dismiss" @click="$emit('dismiss')">
        <v-icon small>mdi-close</v-icon>
      </gg-btn>
    </div>
    <pre v-if="showOutput" ref="out" class="kvm-dop__out">{{ displayOutput || 'Waiting for output…' }}</pre>
  </div>
</template>

<script>
import kvm from '@/api/kvm'

// 3s keeps a long pull well inside the per-service API rate limit (60/min).
const POLL_MS = 3000
// Give up following after ~15 minutes (the job keeps running on the server).
const MAX_POLLS = 300

export default {
  name: 'KvmDockerOperation',
  props: {
    serviceId: { type: [String, Number], required: true },
    // { id, label } as returned by the API when the operation started.
    operation: { type: Object, required: true },
  },
  data: () => ({
    state: 'running',
    exitCode: null,
    output: '',
    showOutput: false,
    timer: null,
    polls: 0,
    lostTrack: false,
  }),
  computed: {
    label () { return this.operation.label || 'Docker operation' },
    stateText () {
      if (this.state === 'running') return this.lostTrack ? 'Still running on the server…' : 'Working…'
      if (this.state === 'succeeded') return 'Done'
      return this.exitCode != null ? `Failed (exit ${this.exitCode})` : 'Failed'
    },
    // Image pulls print one progress line per layer per tick; keep only the
    // latest line for each layer so the useful lines stay visible.
    displayOutput () {
      const lines = (this.output || '').split('\n')
      const seen = new Map()
      const out = []
      for (const line of lines) {
        const m = line.match(/^\s*([0-9a-f]{12}) (Pulling|Downloading|Extracting|Waiting|Verifying|Download complete|Pull complete|Already exists)/)
        if (m) {
          if (seen.has(m[1])) out[seen.get(m[1])] = line
          else { seen.set(m[1], out.length); out.push(line) }
        } else {
          out.push(line)
        }
      }
      return out.slice(-80).join('\n').trim()
    },
  },
  watch: {
    displayOutput () {
      this.$nextTick(() => { const el = this.$refs.out; if (el) el.scrollTop = el.scrollHeight })
    },
  },
  methods: {
    async poll () {
      this.timer = null
      this.polls++
      try {
        const op = await kvm.dockerOperation(this.serviceId, this.operation.id)
        this.output = op.output || ''
        this.state = op.state
        this.exitCode = op.exit_code
      } catch (e) {
        // 404 = the record is gone (server rebooted); treat as unknown-failed.
        if (e.status === 404) { this.state = 'failed'; this.output = e.message }
      }
      if (this.state !== 'running') {
        if (this.state === 'failed') this.showOutput = true
        this.$emit('done', { id: this.operation.id, state: this.state, exitCode: this.exitCode })
        return
      }
      if (this.polls >= MAX_POLLS) { this.lostTrack = true; return }
      this.timer = setTimeout(this.poll, POLL_MS)
    },
  },
  mounted () {
    this.timer = setTimeout(this.poll, 1500)
  },
  beforeDestroy () {
    if (this.timer) clearTimeout(this.timer)
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-dop {
  border: 1px solid $kvm-border-light;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 8px;

  .theme--dark & {
    border-color: $kvm-border-dark;
  }
}
.kvm-dop--failed {
  border-color: #c62828;
}
.kvm-dop__state {
  @include kvm-secondary-text;
}
.kvm-dop__out {
  margin: 8px 0 0;
  max-height: 260px;
  overflow: auto;
  background: #000;
  color: #cfd6dc;
  font-size: 0.78rem;
  line-height: 1.35;
  padding: 8px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}
.kvm-dop__btn:focus-visible {
  @include kvm-focus-ring;
}
</style>
